/**
 * Shift Roster GraphQL Service
 * Connects to AppSync for shift master, assignments, and attendance cycles.
 * Consumes canonical normalized employee models from User Management (userLoader.ts).
 */

import { generateClient } from 'aws-amplify/api';
import { loadAllUsers, accountIdsFor, SimpleUser } from './userLoader';
import { sameIdentity } from './identity';
import { hasLeftOrganisation } from './employmentStatus';
import { CompOffService } from './compOffService';
import {
  ShiftMaster,
  ShiftAssignment,
  CreateShiftMasterInput,
  UpdateShiftMasterInput,
  CreateShiftAssignmentInput,
  BatchShiftResult,
  EmployeeShiftRow,
  BulkOverrideEmployee,
} from '../types/shiftRoster';

let client: any = null;
const getClient = () => { if (!client) client = generateClient(); return client; };
const getAuthMode = () => ({ authMode: 'userPool' as const });
const getData = (r: any) => ('data' in r ? r.data : null);

/**
 * Amplify's GraphQL client throws an object shaped `{ data, errors }` for GraphQL-level
 * errors (e.g. a Lambda resolver rejecting the request) rather than a normal Error with
 * `.message` — every catch block that only read `err.message` showed a generic fallback
 * instead of the real reason, so a caller had no way to see e.g. "Access denied. Employees
 * cannot access Shift Roster operations." without digging through CloudWatch. This pulls
 * the real message out of either shape.
 */
const graphqlErrorMessage = (err: any, fallback: string): string =>
  err?.errors?.[0]?.message || err?.message || fallback;

/**
 * Administrative logins, which the roster and its reports both leave out.
 *
 * Super_Admin, HR_Admin and Finance_Admin are operator accounts rather than people who work a
 * shift. Manager and Employee stay — a manager works a shift like anyone else.
 *
 * Defined here, once, and imported by both Shift Details and the report service. Two copies of
 * "who counts as staff" is exactly the kind of duplication that lets a grid and its export
 * disagree about headcount, which is the bug this rule was added to fix.
 *
 * Matched on the normalised role, never on the account's name. Several of these accounts are
 * literally called "HR Admin" or "Super Admin Dev", which makes name-matching look tempting and
 * makes it wrong the moment a real employee is called Admin — and it would equally miss an
 * administrator with an ordinary name. Spelling-tolerant because the raw Cognito attribute is free
 * text that mapCognitoRole normalises on a best-effort basis: `Finance_Admin`, `finance admin` and
 * `FinanceAdmin` all have to land in the same bucket.
 */
const ADMINISTRATIVE_ROLE_PATTERNS = [/super[\s_-]*admin/i, /hr[\s_-]*admin/i, /finance[\s_-]*admin/i];

export const isAdministrativeRole = (role?: string): boolean => {
  const value = (role || '').trim();
  // An unknown role is treated as an employee: excluding somebody from the roster because their
  // role failed to normalise would hide a real person.
  if (!value) return false;
  return ADMINISTRATIVE_ROLE_PATTERNS.some(pattern => pattern.test(value));
};

// ─── GraphQL Queries ───

// weekendDays / isNightShift / grace / overtime were missing here, so every ShiftMaster
// arrived with those fields undefined — which is why the Night and Day filters, the night
// stat card and the moon badge all behaved as if no shift was ever a night shift, and why
// the weekend-pattern dropdown only ever offered one option.
const SHIFT_MASTER_FIELDS = `id shiftCode shiftName description startTime endTime breakDuration workingHours
      weekendDays isNightShift lateGraceMinutes earlyExitMinutes overtimeEligible
      color isActive version createdBy updatedBy createdAt updatedAt`;

const listShiftMastersQuery = /* GraphQL */ `
  query ListShiftMasters($limit: Int, $nextToken: String) {
    listShiftMasters(limit: $limit, nextToken: $nextToken) {
      items { ${SHIFT_MASTER_FIELDS} }
      nextToken
    }
  }
`;

const shiftAssignmentsByEmployeeQuery = /* GraphQL */ `
  query ShiftAssignmentsByEmployee($employeeId: String!, $dateFrom: String, $dateTo: String, $limit: Int) {
    shiftAssignmentsByEmployee(employeeId: $employeeId, dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit) {
      items { employeeId date shiftCode attendanceCycleId createdBy updatedBy createdAt updatedAt }
      nextToken
    }
  }
`;

/**
 * The same query with the audit and workflow fields the roster grid does not need.
 *
 * Kept as a separate document rather than widening the selection above, deliberately. `status`,
 * `rosterStatus`, `previousShiftCode` and `changeReason` are all declared on ShiftAssignment in
 * schema.graphql, but a selection set is validated against the schema *as deployed*, and this
 * repo's AppSync schema is applied by a Terraform run that is not guaranteed to have happened in
 * every environment. Widening the shared query would mean an environment one apply behind serves
 * a validation error for the whole roster grid; keeping it separate confines that to Reports,
 * where `assignmentDetailFieldsAvailable` degrades to the narrow selection instead.
 */
const DETAILED_ASSIGNMENT_FIELDS = `
  employeeId date shiftCode status rosterStatus attendanceCycleId
  previousShiftCode changeReason version
  createdBy updatedBy createdAt updatedAt
`;

const BASE_ASSIGNMENT_FIELDS =
  'employeeId date shiftCode attendanceCycleId createdBy updatedBy createdAt updatedAt';

/**
 * One assignments query, composed along the two axes that can be refused independently.
 *
 * Written as a composition rather than four near-identical literals because the two failures are
 * unrelated and each needs its own fallback:
 *
 *   the selection set — `status`, `rosterStatus`, `previousShiftCode`, `changeReason` and
 *     `version` are declared on ShiftAssignment but a selection set is validated against the
 *     schema *as deployed*, and this repo's is applied by a Terraform run that has not
 *     necessarily happened;
 *   the nextToken argument — which the schema did not declare at all until it was added
 *     alongside this. Passing it was rejected as `UnknownArgument`, and because the argument
 *     appeared in *both* of the previous two documents, the "narrow fallback" failed identically.
 *     Reports could therefore never read a single assignment: every employee came back
 *     unreadable and the whole page rendered as weekly offs.
 *
 * Hence four combinations, and hence composing them: a fallback that shares the broken part of
 * the query with the thing it is falling back from is not a fallback.
 */
const assignmentsQuery = (detailed: boolean, paged: boolean): string => {
  const fields = detailed ? DETAILED_ASSIGNMENT_FIELDS : BASE_ASSIGNMENT_FIELDS;
  const args = paged
    ? '$employeeId: String!, $dateFrom: String, $dateTo: String, $limit: Int, $nextToken: String'
    : '$employeeId: String!, $dateFrom: String, $dateTo: String, $limit: Int';
  const call = paged
    ? 'employeeId: $employeeId, dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit, nextToken: $nextToken'
    : 'employeeId: $employeeId, dateFrom: $dateFrom, dateTo: $dateTo, limit: $limit';
  return /* GraphQL */ `
    query ShiftAssignments(${args}) {
      shiftAssignmentsByEmployee(${call}) {
        items { ${fields} }
        nextToken
      }
    }
  `;
};

// ─── GraphQL Mutations ───

const createShiftMasterMutation = /* GraphQL */ `
  mutation CreateShiftMaster($input: CreateShiftMasterInput!) {
    createShiftMaster(input: $input) { ${SHIFT_MASTER_FIELDS} }
  }
`;

const updateShiftMasterMutation = /* GraphQL */ `
  mutation UpdateShiftMaster($input: UpdateShiftMasterInput!) {
    updateShiftMaster(input: $input) { ${SHIFT_MASTER_FIELDS} }
  }
`;

// `deleted` is NOT selected: deleteShiftMaster returns ShiftMaster, which has no such
// field, so asking for it failed the whole mutation at GraphQL validation
// ("FieldUndefined: Field 'deleted' in type 'ShiftMaster' is undefined") before the request
// ever reached the Lambda. Every delete failed, and because the resolver never ran, the
// "N employees still have this shift" guard it exists to report never got a chance to speak.
// The Lambda does put `deleted: true` in its payload, but AppSync drops anything the return
// type does not declare, so nothing was lost by it — id and shiftCode are real ShiftMaster
// fields and are all the caller uses.
const deleteShiftMasterMutation = /* GraphQL */ `
  mutation DeleteShiftMaster($id: ID!) {
    deleteShiftMaster(id: $id) { id shiftCode }
  }
`;

const createShiftAssignmentMutation = /* GraphQL */ `
  mutation CreateShiftAssignment($input: CreateShiftAssignmentInput!) {
    createShiftAssignment(input: $input) { employeeId date shiftCode attendanceCycleId createdBy createdAt }
  }
`;

const deleteShiftAssignmentMutation = /* GraphQL */ `
  mutation DeleteShiftAssignment($employeeId: String!, $date: String!) {
    deleteShiftAssignment(employeeId: $employeeId, date: $date) { employeeId date }
  }
`;

// NOTE: field name/shape here must match the backend Lambda's actual handled
// operation — it switches on fieldName 'batchUpdateShiftAssignments' and takes
// $input (not 'batchCreateShiftAssignments'/$assignments, which nothing implements).
const batchUpdateShiftAssignmentsMutation = /* GraphQL */ `
  mutation BatchUpdateShiftAssignments($input: [CreateShiftAssignmentInput!]!) {
    batchUpdateShiftAssignments(input: $input) {
      successCount
      failureCount
      failedRecords { employeeId date shiftCode error }
      message
    }
  }
`;

export class ShiftRosterService {
  /*
   * ─── Shift master cache ───
   *
   * Shift definitions live in the ShiftMaster table and nowhere else. There is deliberately
   * no built-in set here any more.
   *
   * What used to sit in this space: a hardcoded DEFAULT_SHIFT_MASTERS list, plus logic that
   * rewrote whatever was cached to match it and re-added any code missing from it. That made
   * the source file a second source of truth competing with the table — a shift edited in
   * Shift Configuration could be silently reverted to its shipped hours on the next read,
   * and a shift deleted there would reappear. A GEN→GS rename table had the same problem in
   * reverse: it renamed a code that genuinely exists in the table into one that may not.
   *
   * localStorage below is now purely a cache of what the server returned. It is never seeded,
   * never reconciled against anything, and an empty table legitimately means no shifts —
   * the UI says so rather than inventing a set to display.
   */
  private static readonly SHIFT_MASTERS_KEY = 'ems_shift_masters';

  /** Last known server response. Empty when nothing has been fetched or nothing is defined. */
  static getLocalShiftMasters(): ShiftMaster[] {
    const raw = localStorage.getItem(this.SHIFT_MASTERS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.error('[ShiftRoster] Error parsing cached shift masters:', e);
      return [];
    }
  }

  static saveLocalShiftMasters(shifts: ShiftMaster[]): void {
    localStorage.setItem(this.SHIFT_MASTERS_KEY, JSON.stringify(shifts));
  }

  // ─── Shift Master Operations ───

  static async listShiftMasters(): Promise<ShiftMaster[]> {
    try {
      const res = await getClient().graphql({
        query: listShiftMastersQuery,
        variables: { limit: 100 },
        ...getAuthMode(),
      } as any);
      const items = getData(res)?.listShiftMasters?.items || [];
      // Cache and return whatever the server said, including nothing. The old code only
      // accepted a non-empty response and otherwise fell through to the cache, which meant an
      // intentionally empty table could never be represented — you always got a stale or
      // invented list back.
      this.saveLocalShiftMasters(items);
      return items;
    } catch (error) {
      // Only a failed request falls back to the cache; an empty table is an answer, not a failure.
      console.warn('[ShiftRoster] listShiftMasters GraphQL unavailable, using cached shifts:', error);
      return this.getLocalShiftMasters();
    }
  }

  /*
   * seedStandardShifts and its STANDARD_SHIFTS list are gone.
   *
   * They existed to bulk-create a built-in six-shift set, which is the same hardcoding the
   * cache note above describes: it decided what shifts an organisation has, in source, and
   * would happily re-add a code someone had deliberately removed. Shifts are created and
   * edited in Shift Configuration, which writes proper records with createdBy / updatedBy /
   * version — an audit trail a seeded constant cannot produce.
   */

  /*
   * A failed write throws. It must not be reported as success.
   *
   * This used to catch the GraphQL error, log a console.warn, then fabricate a
   * locally-invented ShiftMaster (random id, version 1) and return it as though the
   * server had accepted it. The caller has proper error handling and shows "Failed to
   * save shift" on a throw — but nothing was ever thrown, so it showed "Shift created"
   * instead. The invented record then went into the localStorage cache and was wiped by
   * the next listShiftMasters, which overwrites the cache with whatever the server said
   * (including nothing). Net effect for an admin: a success toast, and a Shift
   * Configuration page still reading "0 shifts" with no error anywhere on screen.
   *
   * The cache is a read fallback for an outage, not a write destination — the same
   * reasoning as the note on SHIFT_MASTERS_KEY: a shift that exists only in one admin's
   * browser is not a shift the organisation has.
   */
  static async createShiftMaster(input: CreateShiftMasterInput): Promise<ShiftMaster> {
    const res = await getClient().graphql({
      query: createShiftMasterMutation,
      variables: { input },
      ...getAuthMode(),
    } as any);

    const created: ShiftMaster | undefined = getData(res)?.createShiftMaster;
    if (!created) {
      // A 200 with no payload is still a failed write — usually a resolver that is not
      // attached to the field. Surfacing it beats caching a shift nobody else can see.
      throw new Error('Shift was not created — the server accepted the request but returned no record.');
    }

    const current = this.getLocalShiftMasters();
    const filtered = current.filter(s => s.shiftCode.toUpperCase() !== created.shiftCode.toUpperCase());
    this.saveLocalShiftMasters([created, ...filtered]);
    return created;
  }

  /** Throws on failure, for the same reason as createShiftMaster above. */
  static async updateShiftMaster(input: UpdateShiftMasterInput): Promise<ShiftMaster> {
    const res = await getClient().graphql({
      query: updateShiftMasterMutation,
      variables: { input },
      ...getAuthMode(),
    } as any);

    const updated: ShiftMaster | undefined = getData(res)?.updateShiftMaster;
    if (!updated) {
      // Also the shape a rejected optimistic-concurrency check takes (expectedVersion
      // mismatch), which the admin needs told rather than silently applied locally.
      throw new Error('Shift was not updated — the server returned no record. It may have been changed by someone else; reload and retry.');
    }

    const current = this.getLocalShiftMasters();
    this.saveLocalShiftMasters(current.map(s => (s.id === updated.id ? updated : s)));
    return updated;
  }

  /**
   * Throws on failure, for the same reason as createShiftMaster above.
   *
   * The local-only delete was the most misleading of the three: it removed the shift from
   * this admin's cache and reported success, so the shift looked deleted here and stayed
   * live for everyone else until the next list call brought it back.
   */
  static async deleteShiftMaster(id: string): Promise<{ id: string; shiftCode: string }> {
    let res: any;
    try {
      res = await getClient().graphql({
        query: deleteShiftMasterMutation,
        variables: { id },
        ...getAuthMode(),
      } as any);
    } catch (err: any) {
      console.error('[ShiftRoster] deleteShiftMaster threw:', err);
      throw new Error(graphqlErrorMessage(err, 'Could not delete the shift.'));
    }

    if (res?.errors?.length) {
      console.error('[ShiftRoster] deleteShiftMaster returned errors:', res.errors);
      throw new Error(graphqlErrorMessage(res, 'Could not delete the shift.'));
    }

    const resData = getData(res)?.deleteShiftMaster;
    if (!resData) {
      throw new Error('Shift was not deleted — the server returned no record.');
    }

    const current = this.getLocalShiftMasters();
    this.saveLocalShiftMasters(current.filter(s => s.id !== id));
    return resData;
  }

  // ─── Assignment Operations ───

  /**
   * Assignments for every Cognito account belonging to one person, merged by date.
   *
   * Assignments are keyed by Cognito username, and an employee has one account per email
   * domain, so a roster row merged from both must be looked up under each id or the grid
   * renders blank. On a date present under more than one account the earlier id wins —
   * accountIdsFor() returns the surviving account first, making it authoritative.
   */
  static async getAssignmentsForAccounts(
    accountIds: string[],
    dateFrom?: string,
    dateTo?: string
  ): Promise<ShiftAssignment[]> {
    if (accountIds.length === 0) return [];
    if (accountIds.length === 1) {
      return this.getAssignmentsForEmployee(accountIds[0], dateFrom, dateTo);
    }

    const pages = await Promise.all(
      accountIds.map(id => this.getAssignmentsForEmployee(id, dateFrom, dateTo))
    );

    const byDate = new Map<string, ShiftAssignment>();
    for (const page of pages) {
      for (const assignment of page) {
        if (!byDate.has(assignment.date)) byDate.set(assignment.date, assignment);
      }
    }
    return Array.from(byDate.values());
  }

  static async getAssignmentsForEmployee(
    employeeId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<ShiftAssignment[]> {
    try {
      const res = await getClient().graphql({
        query: shiftAssignmentsByEmployeeQuery,
        variables: { employeeId, dateFrom, dateTo, limit: 100 },
        ...getAuthMode(),
      } as any);
      return getData(res)?.shiftAssignmentsByEmployee?.items || [];
    } catch (error) {
      console.error(`[ShiftRoster] getAssignments error for ${employeeId}:`, error);
      return [];
    }
  }

  /**
   * Whether the deployed schema accepted the detailed selection set.
   *
   * Module-level and sticky: once one employee's request has been refused for an unknown field,
   * every other employee in the same report would be refused identically, and a report over a
   * hundred employees should not make a hundred failing round trips to rediscover that.
   */
  private static assignmentDetailFieldsAvailable = true;

  /**
   * Whether the deployed schema accepts the `nextToken` argument on shiftAssignmentsByEmployee.
   *
   * Module-level and sticky for the same reason as the flag above: one employee's refusal predicts
   * every other employee's, and a report over a hundred people should not make a hundred failing
   * round trips to rediscover it.
   *
   * The argument was missing from schema.graphql itself until it was added alongside this, so this
   * starts out false in every environment until that apply has run — which is exactly the state
   * that made Reports show the whole company on weekly offs.
   */
  private static assignmentPaginationAvailable = true;

  /**
   * Whether the audit/workflow assignment fields are being served.
   *
   * Read by the Reports page so it can say the Shift Status and Shift Change columns are blank
   * because the schema is behind, rather than leaving an Admin to conclude the data is missing.
   */
  static detailedAssignmentFieldsAvailable(): boolean {
    return this.assignmentDetailFieldsAvailable;
  }

  /**
   * Every assignment in a date range, following `nextToken` to the end.
   *
   * The roster grid's fetch passes `limit: 100` and ignores `nextToken`, which is correct for the
   * one month it draws — a month cannot exceed 31 rows per employee. Reports are not bounded that
   * way: a full-year range is 365 rows per employee, so the same code would have silently returned
   * the first 100 days and produced a report that looked complete and was missing two thirds of
   * the year. Silent truncation is the one failure mode a report must not have.
   */
  static async getAssignmentsDetailed(
    employeeId: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<ShiftAssignment[]> {
    const items: ShiftAssignment[] = [];
    let nextToken: string | null = null;
    // A stop bound rather than `while (true)`: a server that returned a repeating token would
    // otherwise spin forever. 40 pages of 500 is 20k days for one person — far past any real range.
    for (let page = 0; page < 40; page++) {
      const paged = this.assignmentPaginationAvailable;
      const detailed = this.assignmentDetailFieldsAvailable;
      let res: any;
      try {
        res = await getClient().graphql({
          query: assignmentsQuery(detailed, paged),
          // nextToken is omitted entirely, not passed as null, when the argument is unavailable:
          // an undeclared argument fails validation whatever its value.
          variables: paged
            ? { employeeId, dateFrom, dateTo, limit: 500, nextToken }
            : { employeeId, dateFrom, dateTo, limit: 500 },
          ...getAuthMode(),
        } as any);
      } catch (error: any) {
        const message = graphqlErrorMessage(error, '');

        /*
         * The nextToken argument is not served. Drop pagination and retry.
         *
         * Checked before the selection-set fallback because it is the more specific diagnosis, and
         * because getting the order wrong is what broke this: the selection fallback fired first,
         * dropped to a "narrow" query that still passed nextToken, and failed identically. Every
         * employee then came back unreadable and the report showed the whole company on weekly
         * offs. A fallback has to stop doing the thing that failed.
         */
        if (paged && /unknownargument|unknown field argument/i.test(message)) {
          console.warn(
            '[ShiftRoster] nextToken is not served by the deployed schema; reading assignments '
            + 'as a single page. Apply the AppSync schema to restore pagination. Reason:', message
          );
          this.assignmentPaginationAvailable = false;
          page--;
          continue;
        }

        // A field the deployed schema does not know is a validation error, not a data error:
        // retry this employee once on the narrow selection and remember the answer.
        if (detailed && /field|validation|unknown/i.test(message)) {
          console.warn(
            '[ShiftRoster] Detailed assignment fields rejected by the deployed schema; ' +
            'falling back to the base selection. Shift Status, Roster Status and Shift Change ' +
            'columns will be blank until the AppSync schema is applied. Reason:', message
          );
          this.assignmentDetailFieldsAvailable = false;
          page--;
          continue;
        }

        /*
         * Out of fallbacks: give up loudly.
         *
         * This used to `return items` — an empty array on a first-page failure — which a report
         * cannot tell apart from "this employee has no shifts". The visible result is a row of
         * weekly offs for somebody who worked all week, with nothing on the page saying data was
         * lost. Silent truncation is the one failure mode a report must not have, and returning
         * empty on error is the same fault as returning the first page of many.
         */
        console.error(`[ShiftRoster] getAssignmentsDetailed error for ${employeeId}:`, error);
        throw new Error(
          `Could not read assignments for ${employeeId}: `
          + graphqlErrorMessage(error, 'the roster query failed')
        );
      }

      const conn = getData(res)?.shiftAssignmentsByEmployee;
      const pageItems = conn?.items || [];
      items.push(...pageItems);
      nextToken = conn?.nextToken || null;

      /*
       * Without the argument there is nowhere to send the token back to, so one page is all there
       * is. Said out loud when the page came back full, because that is the case where rows may
       * have been left behind — a report that quietly stops at a page boundary looks complete.
       *
       * In practice this does not truncate: the caller asks per employee for a date range, and
       * 500 rows is more than a year of days for one person. The warning is for the day that
       * assumption stops holding.
       */
      if (!this.assignmentPaginationAvailable) {
        if (nextToken || pageItems.length >= 500) {
          console.warn(
            `[ShiftRoster] ${employeeId} returned a full page with pagination unavailable; `
            + 'some assignments may be missing from this report. Apply the AppSync schema.'
          );
        }
        break;
      }

      if (!nextToken) break;
    }
    return items;
  }

  /**
   * getAssignmentsDetailed across every Cognito account one person owns. Earlier id wins.
   *
   * Throws when *no* account could be read, so the caller can report the gap rather than
   * present an employee's week as empty. A partial success is kept: one of a merged pair's two
   * accounts failing must not discard the assignments the other one returned.
   */
  static async getAssignmentsDetailedForAccounts(
    accountIds: string[],
    dateFrom?: string,
    dateTo?: string
  ): Promise<ShiftAssignment[]> {
    if (accountIds.length === 0) return [];
    const pages = await Promise.allSettled(
      accountIds.map(id => this.getAssignmentsDetailed(id, dateFrom, dateTo))
    );
    const fulfilled = pages.filter(
      (p): p is PromiseFulfilledResult<ShiftAssignment[]> => p.status === 'fulfilled'
    );
    if (fulfilled.length === 0) {
      const first = pages[0] as PromiseRejectedResult | undefined;
      throw new Error(first?.reason?.message || `Could not read assignments for ${accountIds[0]}`);
    }
    const byDate = new Map<string, ShiftAssignment>();
    for (const page of fulfilled) {
      for (const assignment of page.value) {
        if (!byDate.has(assignment.date)) byDate.set(assignment.date, assignment);
      }
    }
    return Array.from(byDate.values());
  }

  /**
   * Persist one day's shift for one employee.
   *
   * Throws when the server did not actually store anything. This used to return
   * `getData(res)?.createShiftAssignment` unchecked, so when the mutation had no resolver
   * behind it — which was the case in every environment, because the appsync module was
   * never passed shift_roster_lambda_arn and the resolvers are gated on it — AppSync
   * answered with a null field and no throw. The caller reported "Shift updated", the cell
   * kept its optimistic value, and the change vanished on the next refresh.
   *
   * A write that cannot be read back is a failure and must say so.
   */
  static async createOrUpdateAssignment(input: CreateShiftAssignmentInput): Promise<ShiftAssignment> {
    let res: any;
    try {
      res = await getClient().graphql({
        query: createShiftAssignmentMutation,
        variables: { input },
        ...getAuthMode(),
      } as any);
    } catch (err: any) {
      console.error('[ShiftRoster] createShiftAssignment threw:', err);
      throw new Error(`Could not save the shift: ${graphqlErrorMessage(err, 'GraphQL error')}`);
    }

    if (res?.errors?.length) {
      console.error('[ShiftRoster] createShiftAssignment returned errors:', res.errors);
      throw new Error(`Could not save the shift: ${graphqlErrorMessage(res, 'GraphQL error')}`);
    }

    const saved = getData(res)?.createShiftAssignment;
    if (!saved) {
      throw new Error(
        'The shift was not saved — the server accepted the request but stored nothing. ' +
        'This usually means the shift-roster resolvers are not deployed.'
      );
    }
    return saved;
  }

  static async saveAssignment(input: CreateShiftAssignmentInput): Promise<ShiftAssignment> {
    return this.createOrUpdateAssignment(input);
  }

  static async batchCreateAssignments(assignments: CreateShiftAssignmentInput[]): Promise<BatchShiftResult> {
    let res: any;
    try {
      res = await getClient().graphql({
        query: batchUpdateShiftAssignmentsMutation,
        variables: { input: assignments },
        ...getAuthMode(),
      } as any);
    } catch (err: any) {
      console.error('[ShiftRoster] batchUpdateShiftAssignments threw:', err);
      throw new Error(graphqlErrorMessage(err, 'Batch update failed.'));
    }

    if (res?.errors?.length) {
      console.error('[ShiftRoster] batchUpdateShiftAssignments returned errors:', res.errors);
      throw new Error(graphqlErrorMessage(res, 'Batch update failed.'));
    }

    return (
      getData(res)?.batchUpdateShiftAssignments ||
      { successCount: 0, failureCount: assignments.length, failedRecords: [], message: 'No response from server' }
    );
  }

  static async batchSaveAssignments(assignments: CreateShiftAssignmentInput[]): Promise<BatchShiftResult> {
    return this.batchCreateAssignments(assignments);
  }

  /**
   * Remove a day's override so the cell falls back to the employee's primary shift.
   *
   * Checks the response rather than returning true on any non-throwing call: with the
   * resolvers undeployed the mutation answered null, this reported success, and the cleared
   * override reappeared on refresh.
   */
  static async deleteShiftAssignment(employeeId: string, date: string): Promise<boolean> {
    let res: any;
    try {
      res = await getClient().graphql({
        query: deleteShiftAssignmentMutation,
        variables: { employeeId, date },
        ...getAuthMode(),
      } as any);
    } catch (err: any) {
      console.error('[ShiftRoster] deleteShiftAssignment threw:', err);
      throw new Error(graphqlErrorMessage(err, 'Could not clear the override.'));
    }

    if (res?.errors?.length) {
      console.error('[ShiftRoster] deleteShiftAssignment returned errors:', res.errors);
      throw new Error(graphqlErrorMessage(res, 'Could not clear the override.'));
    }
    if (!getData(res)?.deleteShiftAssignment) {
      throw new Error(
        'The override was not cleared — the server stored nothing. ' +
        'This usually means the shift-roster resolvers are not deployed.'
      );
    }
    return true;
  }

  // ─── Roster Builder (aggregates data for the grid) ───

  static async getRoster(
    month: number,
    year: number,
    userEmail?: string,
    userRole?: string
  ): Promise<EmployeeShiftRow[]> {
    console.log('[ShiftRoster] getRoster requested for:', { month, year, userEmail, userRole });
    
    // Fetch canonical employee records from User Management Service (Single Source of Truth)
    const employees = await loadAllUsers();
    if (!employees || employees.length === 0) {
      throw new Error('Unable to connect to User Management backend. No employee records returned.');
    }

    /*
     * Everybody sees every employee. Visibility and editability are separate questions here.
     *
     * Managers used to be filtered to their direct reports, which meant a manager could not see
     * the shift a neighbouring team was working — information they need for cover and handover,
     * and which the roster exists to publish. Editing is unchanged and enforced twice: the grid
     * only makes a row's cells interactive when canEditEmployeeRoster allows it, and the backend
     * re-checks who a manager owns — themselves and their reports — on every assignment mutation
     * (managerMayEditEmployee in the shift-roster Lambda), so a wider read grants no additional
     * write.
     *
     * Employees are read-only throughout, by canAccessShiftRoster in the UI and
     * requireRosterAccess in the Lambda.
     *
     * ── …every employee who still works here ─────────────────────────────────────────────
     *
     * People who have left are dropped. A roster is a forward-looking plan, and a leaver's row
     * was a permanent empty lane in it: never assigned, never removable, and counted in every
     * "employees without a team" total on the page. The list only grows, so the grid got
     * steadily less usable with each departure.
     *
     * Note this is the exit date ARRIVING, not the field being filled in — somebody serving
     * notice still has shifts to work and is exactly who a manager is planning cover around.
     * See hasLeftOrganisation, which Leave Reports asks the same question of.
     */
    const todayIso = CompOffService.todayIso();
    const visibleEmployees = employees.filter(emp => !hasLeftOrganisation(emp, todayIso));

    if (visibleEmployees.length === 0) return [];

    // Build date range for month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dateFrom = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;

    // Fetch assignments for all visible employees, across every Cognito account each of
    // them owns — loadAllUsers now merges a person's two domain accounts into one row,
    // and assignments are keyed by username, so querying only the surviving id would
    // render the merged row empty.
    const assignmentPromises = visibleEmployees.map(emp =>
      this.getAssignmentsForAccounts(accountIdsFor(emp), dateFrom, dateTo)
    );
    const allAssignments = await Promise.all(assignmentPromises);

    // Build rows using strictly normalized employee properties (Single Source of Truth)
    return visibleEmployees.map((emp, idx) => {
      const empAssignments = allAssignments[idx] || [];
      const shifts: Record<string, string> = {};
      for (const a of empAssignments) {
        shifts[a.date] = a.shiftCode;
      }
      return {
        // The Cognito id, because assignments are keyed by it — see the note on the type.
        employeeId: emp.id,
        // The HR employee number, for the grid to display instead of that key.
        employeeCode: emp.employeeId || undefined,
        employeeName: emp.fullName || emp.name,
        employeeEmail: emp.email,
        designation: emp.designation || 'Employee',
        department: emp.department || 'General',
        // Carried through from the same SimpleUser field User Management writes, so the roster's
        // Team filter reconciles against HR's maintained list instead of against free text.
        // No default: an unset team is a real state the filter surfaces as "Unassigned", and
        // substituting "General" would invent a team nobody configured.
        team: emp.team || '',
        // Who may edit this row — see canEditEmployeeRoster. Carried on the row because the grid
        // decides editability per employee now that every role sees every employee.
        managerEmail: emp.managerEmail || '',
        // Lets the grid exclude administrative logins — see EmployeeShiftRow.role.
        role: emp.role || '',
        shift: emp.primaryShift,
        primaryShift: emp.primaryShift,
        shifts,
      };
    });
  }

  // ─── Employees (for bulk override) ───

  /**
   * Canonical employee list for Bulk Override selection & filters (Individual / Department /
   * Team / Designation / Shift). All fields below already exist on the canonical SimpleUser
   * model from userLoader — nothing here is a new backend query. "Team" has no distinct field
   * in the employee model (see managerEmail); Bulk Override groups by manager as a stand-in.
   */
  static async getAllEmployees(userEmail?: string, userRole?: string): Promise<BulkOverrideEmployee[]> {
    const employees = await loadAllUsers();
    let filtered = employees;

    if (userRole === 'Manager' && userEmail) {
      // Self as well as reports: a manager edits their own roster row, and their own
      // managerEmail names *their* manager, so the reporting-line test alone excludes them.
      // Same rule as canEditEmployeeRoster, which is what the two tabs actually run.
      filtered = filtered.filter(
        (e: SimpleUser) => sameIdentity(e.email, userEmail) || sameIdentity(e.managerEmail, userEmail)
      );
    }

    return filtered.map((e: SimpleUser) => ({
      id: e.id || e.email,
      name: e.fullName || e.name,
      email: e.email,
      department: e.department || 'General',
      // No default: an unset team is a real state the filter surfaces as "No team set", and
      // substituting a placeholder would invent a team nobody configured.
      team: e.team || '',
      designation: e.designation || 'Employee',
      managerEmail: e.managerEmail || undefined,
      primaryShift: e.primaryShift || undefined,
    }));
  }
}
